import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { UserSession } from '../types/Auth';

type TenantSummary = {
  tenantId: string;
  tenantName: string;
  role: string;
  logoUrl?: string;
};

interface TenantContextType {
  userSession: UserSession | null;
  selectedTenantId: string | null;
  selectedTenant: { tenantId: string; tenantName: string; role: string ; logoUrl?: string} | null;
  setSelectedTenantId: (tenantId: string | null) => void;
  setUserSession: (session: UserSession | null) => void;
 availableTenants: TenantSummary[];
  hasMultipleTenants: boolean;
  isGlobalAdmin: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

const SELECTED_TENANT_KEY = 'pontis_selected_tenant_id';
const SELECTED_TENANT_ROLE_KEY = 'pontis_selected_tenant_role';

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [selectedTenantId, setSelectedTenantIdState] = useState<string | null>(() => {
    // Try to restore from localStorage on mount
    return localStorage.getItem(SELECTED_TENANT_KEY);
  });

  // Use a ref for userSession so setSelectedTenantId has a stable reference
  const userSessionRef = useRef(userSession);
  userSessionRef.current = userSession;

  const setSelectedTenantId = useCallback((tenantId: string | null) => {
    setSelectedTenantIdState(tenantId);
    if (tenantId && userSessionRef.current) {
      // Save tenant ID to localStorage
      localStorage.setItem(SELECTED_TENANT_KEY, tenantId);
      
      // Save tenant role to localStorage (Step 3 requirement)
      const tenant = userSessionRef.current.tenants.find(t => t.tenantId === tenantId);
      if (tenant) {
        localStorage.setItem(SELECTED_TENANT_ROLE_KEY, tenant.role);
      }
    } else {
      localStorage.removeItem(SELECTED_TENANT_KEY);
      localStorage.removeItem(SELECTED_TENANT_ROLE_KEY);
    }
  }, []);

  // Extract available tenants from current user
  const availableTenants = React.useMemo(() => {
    if (!userSession) return [];
    
    const tenantList = userSession.tenants.map(t => ({
  tenantId: t.tenantId,
  tenantName: t.tenantName,
  role: t.role,
  logoUrl: t.logoUrl
}));
    
    // Add "Global Admin" option if user is a global admin
    if (userSession.isGlobalAdmin) {
      return [
        {
          tenantId: '__GLOBAL_ADMIN__',
          tenantName: 'Global Admin',
          role: 'GlobalAdmin',
          logoUrl: undefined
        },
        ...tenantList
      ];
    }
    
    return tenantList;
  }, [userSession]);

  const selectedTenant = React.useMemo(() => {
  if (!selectedTenantId) return null;
  return availableTenants.find(t => t.tenantId === selectedTenantId) ?? null;
}, [selectedTenantId, availableTenants]);
  const hasMultipleTenants = availableTenants.length > 1;
  const isGlobalAdmin = userSession?.isGlobalAdmin ?? false;

  // Auto-select tenant if only one available
  useEffect(() => {
    if (!userSession) return;

    // Don't auto-select if user has multiple options (including Global Admin)
    if (availableTenants.length === 1 && !selectedTenantId) {
      setSelectedTenantId(availableTenants[0].tenantId);
    } else if (availableTenants.length === 0) {
      // User has no tenants, clear selection
      setSelectedTenantId(null);
    } else if (selectedTenantId) {
      // Validate that selected tenant still exists
      const tenantExists = availableTenants.some(t => t.tenantId === selectedTenantId);
      if (!tenantExists) {
        setSelectedTenantId(null);
      }
    }
  }, [userSession, availableTenants, selectedTenantId, setSelectedTenantId]);

 const value = React.useMemo<TenantContextType>(() => ({
  userSession,
  selectedTenantId,
  selectedTenant,
  setSelectedTenantId,
  setUserSession,
  availableTenants,
  hasMultipleTenants,
  isGlobalAdmin,
}), [
  userSession,
  selectedTenantId,
  selectedTenant,
  setSelectedTenantId,
  availableTenants,
  hasMultipleTenants,
  isGlobalAdmin
]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
